import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullseye, faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";
import '../assets/css/ShootoutInfoBoard.css';
import { playCountdownBeep, stopCountdownBeep } from '../utils/sound';

const ShootoutInfoBoard = ({ gameState, game, calculateDisplayTime, calculateShotRemaining, formatTime }) => {
    const [shotTime, setShotTime] = useState(0);
    const [globalTime, setGlobalTime] = useState('10:00');
    const [progress, setProgress] = useState(100);
    const lastBeepTime = useRef(null);
    const calculateShotRemainingRef = useRef(calculateShotRemaining);
    const calculateDisplayTimeRef = useRef(calculateDisplayTime);
    const formatTimeRef = useRef(formatTime);
    const gameStateRef = useRef(gameState);

    useEffect(() => {
        calculateShotRemainingRef.current = calculateShotRemaining;
        calculateDisplayTimeRef.current = calculateDisplayTime;
        formatTimeRef.current = formatTime;
        gameStateRef.current = gameState;
    });

    useEffect(() => {
        const update = () => {
            const remainingMs = calculateShotRemainingRef.current();
            const sec = Math.max(0, Math.ceil(remainingMs / 1000));
            setShotTime(sec);

            if (!gameStateRef.current.timerSoundEnabled) {
                stopCountdownBeep();
            } else if (sec > 5) {
                stopCountdownBeep();
                lastBeepTime.current = null;
            } else if (sec === 0) {
                stopCountdownBeep();
                lastBeepTime.current = null;
            } else if (sec <= 5 && sec > 0 && sec !== lastBeepTime.current) {
                if (sec === 5) {
                    playCountdownBeep();
                }
                lastBeepTime.current = sec;
            }

            const maxTime = gameStateRef.current.currentShotLimitMs || 15000;
            const prog = Math.max(0, Math.min(100, (remainingMs / maxTime) * 100));
            setProgress(prog);

            const currentTime = calculateDisplayTimeRef.current();
            setGlobalTime(formatTimeRef.current(currentTime));
        };

        update();
        const intervalId = setInterval(update, 100);
        return () => clearInterval(intervalId);
    }, []);

    const raceTo = game && game.race_to ? parseInt(game.race_to) : 0;
    const isWarning = shotTime <= 5;
    const timerColor = isWarning ? '#ff0000' : '#00c6ff';

    return (
        <div className="shootout-info-wrapper">
            <div className="shootout-content">
                {/* Header */}
                <div className="header-bar">
                    <div className="home-logo-container">
                        <FontAwesomeIcon icon={faBullseye} className="home-logo-icon" />
                        <h1 className="home-logo-text">CueClub Manager</h1>
                    </div>

                    {/* Full Screen Toggle */}
                    <div className="fullscreen-toggle-container">
                        <button
                            className="fullscreen-btn"
                            onClick={() => {
                                if (!document.fullscreenElement) {
                                    document.documentElement.requestFullscreen().catch(err => {
                                        console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
                                    });
                                } else {
                                    if (document.exitFullscreen) {
                                        document.exitFullscreen();
                                    }
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={!document.fullscreenElement ? faExpand : faCompress} />
                        </button>
                    </div>

                    <div className="header-logo">
                        {game && game.club_logo ? (
                            <img src={game.club_logo} alt="Club Logo" style={{ height: '5vh', objectFit: 'contain' }} />
                        ) : null}
                    </div>
                </div>

                <div className="shootout-info-top">
                    {/* Shot Timer */}
                    <div className="shot-timer-container">
                        <div className="shot-timer-glass">
                            <svg className="shot-timer-ring" viewBox="0 0 100 100">
                                <circle
                                    className="shot-timer-ring-bg"
                                    cx="50"
                                    cy="50"
                                    r="45"
                                />
                                <circle
                                    className="shot-timer-ring-glow"
                                    cx="50"
                                    cy="50"
                                    r="45"
                                    style={{
                                        stroke: timerColor,
                                        strokeDasharray: 283,
                                        strokeDashoffset: 283 - (283 * progress) / 100
                                    }}
                                />
                                <circle
                                    className="shot-timer-ring-progress"
                                    cx="50"
                                    cy="50"
                                    r="45"
                                    style={{
                                        stroke: timerColor,
                                        strokeDasharray: 283, // 2 * PI * 45
                                        strokeDashoffset: 283 - (283 * progress) / 100
                                    }}
                                />
                            </svg>
                            <span
                                className="shot-timer-value"
                                style={{
                                    color: isWarning ? '#ff0000' : '#ffffff',
                                    textShadow: `0 0 20px ${timerColor}99` // Add transparency to shadow
                                }}
                            >
                                {shotTime}
                            </span>
                        </div>
                    </div>

                    {/* Global Timer & Race To Pill */}
                    <div className="timer-row">
                        <div className="global-timer-group">
                            <span className="global-timer-label">TEMPS RESTANT</span>
                            <span className="global-timer-value">{globalTime}</span>
                        </div>

                        <div className="divider"></div>

                        <div className="race-to-group">
                            <span className="race-to-label">RACE TO</span>
                            <span className="race-to-value">{raceTo}</span>
                        </div>
                    </div>
                </div>

                {/* Player Scores */}
                <div className="shootout-info-scores">
                    <div className={`player-panel ${gameState.timerOwner === 1 ? 'active' : ''}`}>
                        <div className="player-name">{gameState.firstPlayer}</div>
                        <div className="player-score-box">
                            <span className="player-score">{gameState.player1FramesWon}</span>
                        </div>
                    </div>
                    <div className={`player-panel ${gameState.timerOwner === 2 ? 'active' : ''}`}>
                        <div className="player-name">{gameState.secondPlayer}</div>
                        <div className="player-score-box">
                            <span className="player-score">{gameState.player2FramesWon}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShootoutInfoBoard;
